import { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import RoomIcon from '@material-ui/icons/Room'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import Axios from 'axios'

import T from '../../home-new/tokens'
import getStatusMeta from './trackingStatusMeta'

// Expanded detail card for the NEW-design (?new) tracking results. Presentation
// only — all fields come from the same tracking API item the old design uses
// (courier_tracking_code / tracking_code / order_status / state[] /
// origin_postcode / destination_postcode / courier_logo). Built to the mockup
// (new-design-other/tracking): status pill top-left, "ขนส่งโดย" + courier logo
// top-right, chevron-up collapse button, large blue courier tracking number,
// origin/destination boxes, then a "ประวัติการจัดส่ง" vertical timeline.

const TH_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]
const EN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

// Courier logo lookup for the ?new tracking detail.
//
// Uses GET /api/courier-info (→ /courier/api/information/th), which returns a
// flat list of ~54 couriers each with a real `image` URL on S3. The old
// POST /api/get-single-courier-logo endpoint is NOT used here because it
// fails to resolve these codes ({} for "FLE", {image:false} for "FLEP").
// components/tracking/DetailCard.js (the OLD design) still uses that endpoint
// and is intentionally left untouched.
//
// The list is fetched ONCE per page load and shared by every expanded result
// (module-level promise cache) so multiple open cards don't refetch it.
let courierInfoPromise = null
const getCourierInfoList = () => {
  if (!courierInfoPromise) {
    courierInfoPromise = Axios.get('/api/courier-info')
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        // Allow a later retry rather than caching the failure forever.
        courierInfoPromise = null
        return []
      })
  }
  return courierInfoPromise
}

// EXACT courier_code match first; otherwise the LONGEST-PREFIX match, since a
// tracking payload may carry a more specific code than the list has
// (e.g. "FLEP" → "FLE"). Returns { image, name } — `name` is the matched
// courier_name, used for the img alt since domestic tracking payloads don't
// carry one. Returns empty image when nothing matches, so the caller keeps
// its existing courier-name/code text fallback.
const resolveCourierLogo = async (courierCode) => {
  const none = { image: '', name: '' }
  if (!courierCode) return none
  const list = await getCourierInfoList()
  const code = String(courierCode).toUpperCase()
  const exact = list.find(
    (c) => String(c.courier_code || '').toUpperCase() === code
  )
  const match =
    exact && exact.image
      ? exact
      : list
          .filter((c) => {
            const cc = String(c.courier_code || '').toUpperCase()
            return cc && code.startsWith(cc)
          })
          .sort(
            (a, b) =>
              String(b.courier_code).length - String(a.courier_code).length
          )[0]
  if (!match || !match.image) return none
  return { image: match.image, name: match.courier_name || '' }
}

// Handles both domestic ("2026-07-27 17:17:22", local) and inter ISO strings.
const formatDateTime = (raw, lang) => {
  if (!raw) return ''
  const d = new Date(String(raw).replace(' ', 'T'))
  if (isNaN(d.getTime())) return raw
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (lang === 'th') {
    return `${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${d.getFullYear() + 543} ${hh}:${mm}`
  }
  return `${d.getDate()} ${EN_MONTHS[d.getMonth()]} ${d.getFullYear()} ${hh}:${mm}`
}

const useStyles = makeStyles((theme) => ({
  panel: {
    position: 'relative',
    background: T.color.surface,
    border: `1px solid ${T.color.line}`,
    borderRadius: T.radius.lg,
    boxShadow: T.shadow.card,
    padding: '32px 36px',
    fontFamily: T.font.body,
    [theme.breakpoints.down('xs')]: { padding: '20px 18px' },
  },
  collapseBtn: {
    position: 'absolute',
    top: 24,
    right: 28,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.color.brand,
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,158,255,0.35)',
    transition: 'transform 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)' },
    [theme.breakpoints.down('xs')]: { top: 16, right: 16, width: 34, height: 34 },
  },
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    paddingRight: 52,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#fff',
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 1,
    padding: '9px 20px',
    borderRadius: T.radius.pill,
    [theme.breakpoints.down('xs')]: { fontSize: 13, padding: '8px 16px' },
  },
  carrierBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  carrierLabel: { fontSize: 14, color: T.color.muted },
  carrierLogo: { maxHeight: 46, maxWidth: 130, objectFit: 'contain' },
  bigTracking: {
    fontFamily: T.font.heading,
    fontWeight: 700,
    fontSize: 34,
    color: T.color.brand,
    margin: '18px 0 0',
    wordBreak: 'break-all',
    [theme.breakpoints.down('xs')]: { fontSize: 24 },
  },
  divider: {
    border: 0,
    borderTop: `1px solid ${T.color.line}`,
    margin: '24px 0',
  },
  odGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  odBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    background: '#F0F1F2',
    borderRadius: T.radius.md,
    padding: '18px 20px',
  },
  odPin: { color: T.color.brand, fontSize: 22, flexShrink: 0, marginTop: 2 },
  odLabel: { fontSize: 14, color: T.color.muted, marginBottom: 4 },
  odValue: { fontSize: 16, color: T.color.ink, fontWeight: 600 },
  historyHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: '32px 0 20px',
    fontFamily: T.font.heading,
    fontWeight: 700,
    fontSize: 22,
    color: T.color.ink,
    [theme.breakpoints.down('xs')]: { fontSize: 18 },
  },
  historyClock: { color: T.color.brand, fontSize: 24 },
  timeline: { position: 'relative', paddingLeft: 8 },
  node: {
    position: 'relative',
    display: 'flex',
    gap: '20px',
    paddingBottom: 8,
  },
  rail: {
    position: 'relative',
    width: 25,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  dot: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#fff',
    border: `3px solid ${T.color.brand}`,
    marginTop: 20,
    zIndex: 1,
  },
  dotActive: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: T.color.brand,
    border: '5px solid #CCECFF',
    marginTop: 18,
    zIndex: 1,
  },
  connector: {
    position: 'absolute',
    top: 24,
    bottom: -8,
    left: '50%',
    width: 2,
    marginLeft: -1,
    background: '#E0E0E0',
  },
  nodeCard: {
    flex: 1,
    background: '#fff',
    border: `1px solid ${T.color.line}`,
    borderRadius: T.radius.md,
    padding: '16px 20px',
    marginBottom: 16,
  },
  nodeTopRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
  },
  nodeStatus: {
    fontWeight: 700,
    fontSize: 17,
    color: T.color.ink,
  },
  nodeStatusActive: { color: T.color.brand },
  nodeDate: { fontSize: 14, color: T.color.faint, whiteSpace: 'nowrap' },
  nodeLoc: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: 8,
    fontSize: 14,
    color: T.color.muted,
  },
  nodeLocPin: { fontSize: 16, color: T.color.faint },
  evidence: { marginTop: 12 },
  evidenceLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 14,
    color: T.color.muted,
    marginBottom: 8,
  },
  signatureImg: {
    maxWidth: 200,
    maxHeight: 80,
    objectFit: 'contain',
    border: `1px solid ${T.color.line}`,
    borderRadius: T.radius.sm,
    background: '#fff',
    padding: 6,
  },
}))

const TrackingDetailNew = ({ item, timeline, t, lang, onCollapse }) => {
  const classes = useStyles()
  const [logoSrc, setLogoSrc] = useState(item.courier_logo || '')
  const [logoName, setLogoName] = useState('')

  useEffect(() => {
    let active = true
    // International records already carry a resolved logo URL — prefer it and
    // skip the lookup entirely.
    if (item.courier_logo) {
      setLogoSrc(item.courier_logo)
      return undefined
    }
    const fetchLogo = async () => {
      const { image, name } = await resolveCourierLogo(item.courier_code)
      if (active && image) {
        setLogoSrc(image)
        setLogoName(name)
      }
    }
    fetchLogo()
    return () => {
      active = false
    }
  }, [item])

  const meta = getStatusMeta(item.order_status, item.delivery_type, lang)

  // Most-recent-first; node 0 is the active (top) status.
  const nodes = Array.isArray(timeline)
    ? [...timeline].sort(
        (a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()
      )
    : []

  // origin/destination + "delivery history" copy for the ?new variant lives in
  // the tracking namespace under `new.detail.*` (separate from the shared keys
  // the OLD design reads). `t` comes from the parent's useTranslation('tracking')
  // and already resolves against the active language.
  const LBL = {
    origin: t('new.detail.origin'),
    destination: t('new.detail.destination'),
    history: t('new.detail.history'),
  }

  const renderOD = (label, value) => (
    <div className={classes.odBox}>
      <RoomIcon className={classes.odPin} />
      <div>
        <div className={classes.odLabel}>{label}</div>
        <div className={classes.odValue}>{value}</div>
      </div>
    </div>
  )

  const origin = item.origin_postcode
  const destination = item.destination_postcode

  return (
    <div className={classes.panel}>
      <button
        type="button"
        className={classes.collapseBtn}
        aria-label="collapse"
        onClick={onCollapse}
      >
        <KeyboardArrowUpIcon />
      </button>

      <div className={classes.headRow}>
        <span className={classes.pill} style={{ backgroundColor: meta.pillBg }}>
          {meta.text}
        </span>
        <div className={classes.carrierBox}>
          <span className={classes.carrierLabel}>{t('courier')}</span>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={item.courier_name || logoName || item.courier_code}
              className={classes.carrierLogo}
            />
          ) : (
            <span className={classes.odValue}>
              {item.courier_name || item.courier_code}
            </span>
          )}
        </div>
      </div>

      <h3 className={classes.bigTracking}>
        {item.courier_tracking_code || item.tracking_code}
      </h3>

      <hr className={classes.divider} />

      {(origin || destination) && (
        <div className={classes.odGrid}>
          {origin && renderOD(LBL.origin, origin)}
          {destination && renderOD(LBL.destination, destination)}
        </div>
      )}

      <div className={classes.historyHead}>
        <AccessTimeIcon className={classes.historyClock} />
        {LBL.history}
      </div>

      <div className={classes.timeline}>
        {nodes.map((node, i) => {
          const isActive = i === 0
          const isLast = i === nodes.length - 1
          return (
            <div className={classes.node} key={`${node.datetime}-${i}`}>
              <div className={classes.rail}>
                <div className={isActive ? classes.dotActive : classes.dot} />
                {!isLast && <div className={classes.connector} />}
              </div>
              <div className={classes.nodeCard}>
                <div className={classes.nodeTopRow}>
                  <span
                    className={`${classes.nodeStatus} ${
                      isActive ? classes.nodeStatusActive : ''
                    }`}
                  >
                    {node.description}
                  </span>
                  <span className={classes.nodeDate}>
                    {formatDateTime(node.datetime, lang)}
                  </span>
                </div>
                {node.location && (
                  <div className={classes.nodeLoc}>
                    <RoomOutlinedIcon className={classes.nodeLocPin} />
                    {node.location}
                  </div>
                )}
                {node.signature && (
                  <div className={classes.evidence}>
                    <div className={classes.evidenceLabel}>
                      <RoomOutlinedIcon className={classes.nodeLocPin} />
                      {t('receiver')} {node.receiver}
                    </div>
                    <img
                      src={node.signature}
                      alt="signature"
                      className={classes.signatureImg}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default TrackingDetailNew
